import { QuoteStatus, SnapQuoteStatus } from '@prisma/client';
import { z } from 'zod/v4';
import type { $ZodEnumParams } from 'zod/v4/core';

export type SortOrder = 'asc' | 'desc';

export const uuid = z.uuid();
export const externalId = z.string().nullish();

export const createdByUserId = {
  createdByUserId: z.uuid(),
};

export const createdByUserUuid = {
  createdByUserUuid: z.uuid(),
};

export const createdById = {
  createdById: z.string().nullish(),
};

export const timestamps = {
  createdAt: z.date(),
  updatedAt: z.date(),
};

export const softDelete = {
  deletedByUserUuid: z.uuid().nullish(),
  deletedAt: z.date().nullish(),
};

export const notSoftDeletedWhereFilter = {
  deletedAt: null,
};

export const parsedSortParam = z.array(
  z.object({
    key: z.string(),
    order: z.enum(['asc', 'desc']).optional().default('asc'),
  }),
);

export type ParsedSortParam = z.infer<typeof parsedSortParam>;

const StatusFilter = { ...QuoteStatus, ...SnapQuoteStatus };

const statusFilterSchema = stringToEnum(StatusFilter);

export const searchQuerystringSchema = z.object({
  q: z.string().optional(),
  status: statusFilterSchema.optional(),
  customer: z.string().optional(),
  quoteId: z.string().optional(),
  createdByDisplayName: z.string().optional(),
  sort: z
    .transform(
      (value): z.input<typeof parsedSortParam> =>
        decodeURIComponent(String(value ?? ''))
          .split(',')
          .map((s) => {
            const [key, order] = s.split(':');
            return { key, order: (order as SortOrder) ?? 'asc' };
          }),
    )
    .pipe(parsedSortParam),
});

/**
 * A native enum validator that will accept any string as an input.
 *
 * This is crucial for interop with Prisma Enums. We use the inferred input type
 * of zod schemas as a type constraint for action function return values so we
 * need the flexibility of accepting any string.
 *
 * @maybe-deprecate use `z.enum(EnumLike)` directly, instead
 */
export function stringToEnum<
  T extends Readonly<Record<string, string | number>>,
>(values: T, params?: $ZodEnumParams) {
  return z.string().pipe(z.any().pipe(z.enum(values, params)));
}

export enum SnapSuggestionStatus {
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXCLUDED = 'EXCLUDED',
  UNKNOWN = 'UNKNOWN',
}

export const snapSuggestionStatusSchema = stringToEnum(SnapSuggestionStatus);
